import { Component, OnInit } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { AbstractControl, FormControl, NgForm, ValidationErrors, Validators } from '@angular/forms';
import { ActionAlert } from '../../../../core/dto/actionAlert';
import { AppUtils } from '../../../../core/config/app.utils';
import { AuthService } from '../../../../modules/auth/services/auth.service';
import { LanguageService } from '../../../../core/services/language.service';
import { LoadingService } from '../../../../core/services/loading.service';
import { OpenAiService } from '../../../../core/services/open-ai.service';
import { SettingService } from '../../../../core/services/setting.service';

@Component({
  selector: 'app-itinerary-settings',
  templateUrl: './itinerary-settings.component.html',
  styleUrls: ['./itinerary-settings.component.scss'],
})
export class ItinerarySettingsComponent implements OnInit {
  brand: any;
  brandId: any;
  actionMessage = new ActionAlert();
  msg: any;
  loggedUser: any;
  selectedLanguages: any = [];
  translationLanguages: any;
  selectedDefaultLanguage: any;

  language: any = 'en';
  languageType: any;
  languageField = new FormControl();
  title = '';
  translatedTitle = '';
  isHidden = false;
  tourOpHeading: any[] = [];

  aiApiKeyControl = new FormControl('', [
    Validators.required,
    (control: AbstractControl): ValidationErrors | null =>
      /\s/.test(control.value ?? '') ? { noWhitespace: true } : null,
  ]);
  showApiKey = false;
  hasExistingKey = false;
  isSavingKey = false;

  seasonCategory: { name: string; startMonth: string; endMonth: string }[] = [
    { name: '', startMonth: '', endMonth: '' }
  ];

  months = [
    { label: 'January', value: 'January' },
    { label: 'February', value: 'February' },
    { label: 'March', value: 'March' },
    { label: 'April', value: 'April' },
    { label: 'May', value: 'May' },
    { label: 'June', value: 'June' },
    { label: 'July', value: 'July' },
    { label: 'August', value: 'August' },
    { label: 'September', value: 'September' },
    { label: 'October', value: 'October' },
    { label: 'November', value: 'November' },
    { label: 'December', value: 'December' },
  ];

  constructor(
    private settingService: SettingService,
    private authService: AuthService,
    private languageService: LanguageService,
    private loadingService: LoadingService,
    private openAiService: OpenAiService,
    private translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    this.loadingService.start();
    this.loggedUser = this.authService.getLoggedInUser();
    this.getLanguages().then();
    this.findBrand().then(() => {
      this.addMissingTitles('Tour Executive', this.selectedLanguages);
      this.filterContentByLanguage(this.language);
      this.languageType = this.translationLanguages?.find(
        (l: any) => l.code === this.language
      );
      this.loadingService.stop();
    });
    this.loadOpenAiKey();
    this.languageField.valueChanges.subscribe((lang: any) => {
      this.filterContentByLanguage(lang);
      this.languageType = this.translationLanguages?.find(
        (l: any) => l.code === lang
      );
      this.translateOnInit().then((response: any) => {
        this.translatedTitle = response;
        this.isHidden = false;
      });
    });
  }

  async findBrand(): Promise<any> {
    this.brand = await this.settingService.findBrand(this.loggedUser.userId);
    this.brandId = this.brand._id;
    this.selectedLanguages = this.brand?.selectedLanguages || [];
    this.selectedDefaultLanguage = this.brand?.selectedDefaultLanguage || '';
    this.tourOpHeading = this.brand?.tourOperatorHeading || [];
    this.seasonCategory = this.brand?.seasonCategory?.length
      ? this.brand.seasonCategory
      : [{ name: '', startMonth: '', endMonth: '' }];
  }

  async getLanguages(): Promise<any> {
    this.translationLanguages =
      await this.languageService.getContentLanguages();
  }

  defaultLanguageSet(type: any, isRender?: boolean): void {
    this.loadingService.start();
    try {
      const payload = {
        _id: this.brandId,
        selectedLanguages: this.selectedLanguages,
        selectedDefaultLanguage: this.selectedDefaultLanguage,
        tourOperatorHeading: this.tourOpHeading,
      };
      this.settingService.updateBrand(payload).then(() => {
        this.msg = type;
        this.actionMessage = new ActionAlert(
          true,
          true,
          this.translocoService.translate(
            'settings.preferences.itinerarySettings.messages.updated'
          ),
          'success'
        );
        this.loadingService.stop();
        if (isRender) {
          this.ngOnInit();
        }
      });
    } catch (e) {
      this.msg = type;
      this.actionMessage = new ActionAlert(
        true,
        true,
        this.translocoService.translate(
          'settings.preferences.itinerarySettings.messages.notUpdated'
        ),
        'error'
      );
      this.loadingService.stop();
    }
  }

  getSelectedLanguagesObjectArray(): any {
    return [
      {
        name: this.translocoService.translate(
          'settings.preferences.itinerarySettings.languages.english'
        ),
        code: 'en',
      },
    ];
  }

  getSelectedLanguagesDropdown(): any {
    const arr: any = [];
    this.selectedLanguages?.forEach((lang: string) => {
      arr.push(this.translationLanguages?.find((l: any) => l.code === lang));
    });
    return arr;
  }

  orderByName(list: any[]): any {
    if (list && list.length > 0) {
      return AppUtils.sortOrderByName(list);
    }
  }

  onTitleChange(value: any): void {
    this.title = value;
  }

  filterContentByLanguage(lang: string): void {
    this.title = this.tourOpHeading
      .filter(obj => obj.language === lang)
      .map(obj => obj.name)
      .join(', ');
  }

  async translateOnInit(): Promise<any> {
    try {
      this.loadingService.start();
      const payload = `Translate the following text "${this.title || this.tourOpHeading[0]?.name}" into ${this.languageType?.name || this.translocoService.translate('settings.preferences.itinerarySettings.languages.english')}. Please provide only text in translated form and without " " `;
      const response = await this.openAiService.askChatGPT({ query: payload });
      this.loadingService.stop();
      return response.choices[0].message.content;
    } catch (e) {}
  }

  async updateHeaderTitle(): Promise<any> {
    for (const item of this.tourOpHeading) {
      if (item.language === this.language) {
        item.name = this.title;
      }
    }
    this.defaultLanguageSet(
      this.translocoService.translate(
        'settings.preferences.itinerarySettings.messages.displayName'
      )
    );
  }

  private addMissingTitles(name: string, languages: []): void {
    if (name) {
      languages.forEach((language: string) => {
        const exists = this.tourOpHeading.some(
          title => title.language === language
        );
        if (!exists) {
          this.tourOpHeading.push({ name, language });
        }
      });
    }
  }

  async saveOpenAiKey(openAiKey: string): Promise<any> {
    try {
      this.loadingService.start();
      const payload = { openAiKey };
      const response = await this.settingService.saveOpenAiKey(payload);
      this.loadingService.stop();
      return response;
    } catch (error) {
      this.loadingService.stop();
      throw error;
    }
  }

  async getOpenAiKey(): Promise<any> {
    try {
      this.loadingService.start();
      const response = await this.settingService.getOpenAiKey();
      this.loadingService.stop();
      return response;
    } catch (error) {
      this.loadingService.stop();
      throw error;
    }
  }

  async loadOpenAiKey(): Promise<void> {
    try {
      const response = await this.getOpenAiKey();
      const data = response?.data;
      if (data?.openAiKey) {
        this.aiApiKeyControl.setValue(data.openAiKey);
        this.hasExistingKey = true;
      } else {
        this.hasExistingKey = false;
      }
    } catch (error) {
      this.hasExistingKey = false;
    }
  }

  async onSaveApiKey(): Promise<void> {
    const key = this.aiApiKeyControl.value?.trim();
    if (!key) return;
    try {
      this.isSavingKey = true;
      await this.saveOpenAiKey(key);
      this.hasExistingKey = true;
      this.msg = 'api key';
      this.actionMessage = new ActionAlert(
        true,
        true,
        this.translocoService.translate(
          'settings.preferences.itinerarySettings.messages.apiKeyAdded'
        ),
        'success'
      );
    } catch (error: any) {
      this.msg = 'api key';
      this.actionMessage = new ActionAlert(
        true,
        true,
        error?.error?.message || this.translocoService.translate(
          'settings.preferences.itinerarySettings.messages.apiKeyInvalid'
        ),
        'error'
      );
    } finally {
      this.isSavingKey = false;
    }
  }

  addSeason(): void {
    this.seasonCategory.push({ name: '', startMonth: '', endMonth: '' });
  }

  removeSeason(index: number): void {
    this.seasonCategory.splice(index, 1);
  }

  async onRemoveApiKey(): Promise<void> {
    // prevent multiple clicks / unnecessary calls
    if (this.isSavingKey) return;

    try {
      // if there's no existing key we can short-circuit
      if (!this.hasExistingKey) return;

      this.isSavingKey = true;
      await this.settingService.removeOpenAiKey();


      this.aiApiKeyControl.setValue('');
      this.hasExistingKey = false;

      this.msg = 'api key';
      this.actionMessage = new ActionAlert(
        true,
        true,
        this.translocoService.translate(
          'settings.preferences.itinerarySettings.messages.apiKeyRemoved'
        ),
        'success'
      );
    } catch (error: any) {
      this.msg = 'api key';
      this.actionMessage = new ActionAlert(
        true,
        true,
        error?.error?.message || this.translocoService.translate(
          'settings.preferences.itinerarySettings.messages.apiKeyInvalid'
        ),
        'error'
      );
    } finally {
      this.isSavingKey = false;
    }
  }

  async onUpdateSeasons(form: NgForm): Promise<void> {
    if (form.invalid) {
      return;
    }
    this.loadingService.start();
    try {
      const payload = {
        _id: this.brandId,
        seasonCategory: this.seasonCategory,
      };
      await this.settingService.updateSeasonCategory(payload);
      this.msg = 'season category';
      this.actionMessage = new ActionAlert(
        true,
        true,
        this.translocoService.translate(
          'settings.preferences.itinerarySettings.messages.updated'
        ),
        'success'
      );
      this.loadingService.stop();
    } catch (e) {
      this.msg = 'season category';
      this.actionMessage = new ActionAlert(
        true,
        true,
        this.translocoService.translate(
          'settings.preferences.itinerarySettings.messages.notUpdated'
        ),
        'error'
      );
      this.loadingService.stop();
    }
  }
}
